import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, useKeyboardModifiers } from '@prio365/prio365-react-library';
import { Empty, notification } from 'antd';
import classNames from 'classnames';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { useTheme } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentFolderChildren,
  getCurrentFolderItem,
  getDocumentSettings,
  getDriveItemIsFetching,
  getErrorOfDriveItem,
  getProjectByIdState,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import Flex from '../../../../components/Flex';
import PrioSpinner from '../../../../components/PrioSpinner';
import VirtualTable2, {
  LoadingProps,
  VColumn,
} from '../../../../components/VirtualTable/VirtualTable2';
import {
  DND_TYPE_DRIVE_ITEM_FILE,
  DND_TYPE_EMAIL,
  DND_TYPE_EMAIL_ATTACHMENT,
  DND_TYPE_PROMAN_REPORT,
  DndDriveItemDropResult,
  DndDriveItemDto,
  DndPromanReportDto,
} from '../../../../dnd/types';
import { DriveUserRemoteItem } from '../../../../models/Document';
import {
  DriveItem,
  DriveItemDropTarget,
  FolderDriveItem,
} from '../../../../models/Drive';
import { Project } from '../../../../models/Project';
import { DriveItemId, GroupId, ProjectId } from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import { apiFetchSavedAttachmentMetadata } from '../../../mail/api';
import {
  DriveItemsFetchContext,
  copyItemsIntoFolder,
  fetchDriveItemsSagaAction,
} from '../../actions';
import { addPrioFilesToUploadList } from '../../actions/uploadLists';
import { apiFetchDocumentSearchResults, apiUnzipArchive } from '../../api';
import {
  sagaStartUploadFiles,
  sagaUploadFiles,
} from '../../sagas/watchUploadFiles';
import { useDriveItemActions } from '../../hooks/useDriveItemActions';
import {
  checkDriveItemNameConflicts,
  checkIfOneNote,
  checkUploadConflicts,
  dispatchUploadFiles,
  folderZip,
  getPDFType,
  iconForFile,
  isDriveItemFolder,
  moveOrCopyDriveItems,
  uploadMessagesToDriveFolder,
} from '../../util';
import DocumentsTableContextMenu from '../DocumentsTableContextMenu';
import DocumentsTableRowIcon, {
  useDocumentsTableRowIconStyles,
} from '../DocumentsTableRowIcon';
import { OnMailAttachmentsDrop } from '../DraggableDriveItem';
import { MessageCenterMessage } from '../../../../models/Message';
import ZipFolderModal from '../Modals/ZipFolderModal';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: { width: '100%', height: '100%' },
  menuButton: {
    visibility: 'hidden',
    height: '100%',
    width: '40px',
    '&:hover': {
      backgroundColor: theme.old.components.table.menuButton.backgroundColor,
      color: theme.old.components.table.menuButton.color,
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowMenuButtonIcon: {
    fontSize: theme.old.components.documentsTable.font.fontSize,
  },
  noItemsScreen: { width: '100%', height: '100%', position: 'relative' },
  maskedIcon: {
    '& mask g g': { transform: 'translate(11em, 9em) scale(.75)' },
  },
  noItemsIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '200px',
    height: '200px',
    position: 'absolute',
    left: 'calc(50% - 100px)',
    top: 'calc(50% - 100px)',
  },
  noItemsText: { textAlign: 'center', color: 'rgba(0, 0, 0, 0.45)' },
  rowFile: {
    cursor: 'default',
    '&:hover $menuButton': { visibility: 'visible' },
  },
  columnName: { padding: 0, paddingLeft: 10 },
  columnFile: { width: 40 },
  rowFolder: {
    cursor: 'pointer',
    '&:hover $menuButton': { visibility: 'visible' },
  },
  vTable: { fontSize: theme.old.components.documentsTable.font.fontSize },
}));

interface DocumentsWidgetTableProps {
  groupId: GroupId;
  activeProjectId: ProjectId;
  currentFolderDriveItemId: string;

  onDriveItemClick?: (driveItem: DriveItem, mimeType: string) => void;
  onRowOver?: (isOver: boolean) => void;

  onMenuRename?: (driveItem: DriveItem) => void;
  onMenuDelete?: (driveItem: DriveItem) => void;
  onMenuDownload?: (driveItem: DriveItem) => void;
  onMenuCopy?: (driveItem: DriveItem) => void;
  onMenuMove?: (driveItem: DriveItem) => void;
  onCopyAsPdf?: (driveItem: DriveItem) => void;
  onDndMove?: (
    item: unknown,
    monitor: DropTargetMonitor<unknown, unknown>
  ) => void;
  onDndCopy?: (
    item: unknown,
    monitor: DropTargetMonitor<unknown, unknown>
  ) => void;
  onMailAttachmentsDrop?: OnMailAttachmentsDrop;
  searchInput?: string;
  setSearchInput?: (value: string) => void;
  setIsKeywordSearch?: (value: boolean) => void;
  isKeywordSearch?: boolean;
  searchDriveItemId?: DriveItemId;
  setSearchDriveItemId?: (value: DriveItemId) => void;
  isSearchingDocuments?: boolean;
  setIsSearchingDocuments?: (value: boolean) => void;
  setProjectIdFromUrl?: (value: ProjectId) => void;
  onOpenParentFolder?: (driveItem: DriveItem) => void;
  onEditFolderAccessRights: (driveItem: DriveItem) => void;
  onDownloadXInvoice: (driveItem: DriveItem) => void;
}

export const DocumentsWidgetTable: React.FC<DocumentsWidgetTableProps> = (
  props
) => {
  //#region -------------------------------- Variables
  const classes = useStyles();
  const documentsTableRowIconClasses = useDocumentsTableRowIconStyles();
  const { t } = useTranslation();
  const {
    groupId,
    activeProjectId,
    currentFolderDriveItemId,
    onDriveItemClick: handleDriveItemClick,
    onMenuRename,
    onMenuDelete,
    onCopyAsPdf,
    onMenuDownload,
    onMenuCopy,
    onMenuMove,
    onDndCopy,
    onDndMove,
    onMailAttachmentsDrop,
    onRowOver,
    searchInput,
    setSearchInput,
    setIsKeywordSearch,
    searchDriveItemId,
    setSearchDriveItemId,
    isSearchingDocuments,
    setIsSearchingDocuments,
    setProjectIdFromUrl,
    isKeywordSearch,
    onOpenParentFolder,
    onEditFolderAccessRights,
    onDownloadXInvoice,
  } = props;

  const tableId = 'widgetBar';
  const dispatch = useDispatch();

  const { altIsPressed } = useKeyboardModifiers();

  const { openInBrowser, openLocally } = useDriveItemActions(
    groupId,
    activeProjectId
  );
  //#endregion

  //#region -------------------------------- State declaration
  const [selectedDriveItems, setSelectedDriveItems] = useState<DriveItem[]>([]);
  const [driveItemsFromSearch, setDriveItemsFromSearch] = useState<DriveItem[]>(
    []
  );
  const [zipModalVisible, setZipModalVisible] = useState<boolean>(false);
  const [zipDriveItem, setZipDriveItem] = useState<DriveItem>(null);
  const [noResult, setNoResult] = useState<boolean>(false);
  const isRoot = !currentFolderDriveItemId;
  const documentsSettings = useSelector(getDocumentSettings);
  const rootDriveItemFolderId = `root-group-${groupId}`;

  const projectsById = useSelector(getProjectByIdState);

  const theme = useTheme<PrioTheme>();

  //#endregion

  //#region -------------------------------- Selectors
  const driveItems = useSelector<RootReducerState, DriveItem[]>((state) =>
    getCurrentFolderChildren(
      state,
      searchDriveItemId ?? currentFolderDriveItemId ?? `root-group-${groupId}`
    )
  );

  const rootFolderDriveItem = useSelector<RootReducerState, DriveItem>(
    (state) =>
      getCurrentFolderItem(
        state,
        currentFolderDriveItemId ?? `root-group-${groupId}`
      )
  );

  const { isFetching, isFetchingWithNextLink } = useSelector<
    RootReducerState,
    { isFetching: boolean; isFetchingWithNextLink: boolean }
  >((state) =>
    getDriveItemIsFetching(
      state,
      isRoot ? `root-group-${groupId}` : currentFolderDriveItemId
    )
  );

  const dataToForceRender = useMemo(
    () => ({
      groupId,
      isRoot,
      driveItemIds: (driveItemsFromSearch
        ? driveItemsFromSearch
        : (driveItems ?? [])
      ).map(({ id }) => id),
      documentListSpacing: documentsSettings.documentListSpacing,
    }),
    [
      groupId,
      isRoot,
      driveItemsFromSearch,
      driveItems,
      documentsSettings.documentListSpacing,
    ]
  );

  const errorMessage = useSelector<RootReducerState, string>((state) =>
    getErrorOfDriveItem(
      state,
      searchDriveItemId ?? currentFolderDriveItemId ?? `root-group-${groupId}`
    )
  );

  // Handler for dropping on empty area of table
  const onTableDrop = useCallback(
    (item: any, monitor: DropTargetMonitor<unknown, unknown>) => {
      if (monitor.didDrop()) {
        return undefined;
      }
      const itemType = monitor.getItemType();

      switch (itemType) {
        case DND_TYPE_PROMAN_REPORT: {
          // Drop into current folder when dropping on empty area
          handlePromanReportDropToFolder(
            item as DndPromanReportDto,
            currentFolderDriveItemId || rootDriveItemFolderId
          );
          return undefined;
        }
        case DND_TYPE_EMAIL_ATTACHMENT: {
          if (onMailAttachmentsDrop) {
            const currentFolderItem =
              driveItems?.find(
                (item) => item.id === currentFolderDriveItemId
              ) || rootFolderDriveItem;
            onMailAttachmentsDrop(
              item.draggableMessageAttachment,
              currentFolderItem,
              groupId,
              item.projectId,
              item.selectedAttachments,
              false
            );
          }
          return undefined;
        }
      }
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentFolderDriveItemId,
      rootDriveItemFolderId,
      driveItems,
      rootFolderDriveItem,
      onMailAttachmentsDrop,
      groupId,
      // handlePromanReportDropToFolder ist stabil no Memoization needed
    ]
  );

  // Drop target for the entire table area
  const [{ isOver }, drop] = useDrop({
    accept: [DND_TYPE_EMAIL_ATTACHMENT, DND_TYPE_PROMAN_REPORT],
    drop: onTableDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });
  //#endregion

  //#region -------------------------------- Methods
  const getLoading = (): LoadingProps => {
    if (
      isFetching &&
      !isFetchingWithNextLink &&
      driveItems &&
      driveItems.length === 0
    )
      return { loadingType: 'table', indicator: <PrioSpinner alignSelf /> };

    if (isFetchingWithNextLink)
      return { loadingType: 'footer', indicator: <PrioSpinner alignSelf /> };

    return null;
  };

  //#region -------------------------------- Handle methods
  const rowClass = (row: DriveItem): string => {
    const isOneNoteFile = checkIfOneNote(row);
    return isDriveItemFolder(row) && !isOneNoteFile
      ? classes.rowFolder
      : classes.rowFile;
  };
  const onDrop = (
    item: any,
    monitor: DropTargetMonitor<unknown, unknown>,
    target: FolderDriveItem
  ) => {
    if (monitor.didDrop()) {
      return undefined;
    }
    const itemType = monitor.getItemType();

    switch (itemType) {
      case DND_TYPE_DRIVE_ITEM_FILE: {
        return { type: 'driveItem', object: target } as DriveItemDropTarget;
      }
      case DND_TYPE_EMAIL_ATTACHMENT: {
        if (onMailAttachmentsDrop) {
          onMailAttachmentsDrop(
            item.draggableMessageAttachment,
            target.driveItem,
            groupId,
            item.projectId,
            item.selectedAttachments,
            true
          );
        }
        return undefined;
      }
      case DND_TYPE_PROMAN_REPORT: {
        handlePromanReportDropToFolder(
          item as DndPromanReportDto,
          target.driveItem?.id || currentFolderDriveItemId
        );
        return undefined;
      }
      case DND_TYPE_EMAIL: {
        const { selectedMessages, projectId, message } = item as {
          message: MessageCenterMessage;
          selectedMessages: MessageCenterMessage[];
          projectId: string;
          mailFolderId: string;
          isPersonal: boolean;
        };
        uploadMessagesToDriveFolder(
          projectId,
          message,
          target.driveItem?.id,
          groupId,
          selectedMessages,
          dispatch
        );
      }
    }
    return undefined;
  };

  const handlePromanReportDropToFolder = async (
    promanItem: DndPromanReportDto,
    targetFolderId: string
  ) => {
    try {
      // Fetch the blob from the URL
      const response = await fetch(promanItem.blobUrl);
      const blob = await response.blob();

      // Create File object
      const file = new File([blob], promanItem.fileName, {
        type: 'application/pdf',
      });

      // Create PrioFile object like normal file uploads
      const sessionId = `proman-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const prioFile = file as any;
      prioFile.sessionId = sessionId;
      prioFile.fileId = `proman-${promanItem.documentId}-${Date.now()}`;
      prioFile.file = file;

      // Use the same upload system as normal file drops
      const prioFiles = [prioFile];

      // Get the correct folder children for conflict checking
      const targetFolderChildren =
        targetFolderId === currentFolderDriveItemId || !targetFolderId
          ? (driveItems ?? [])
          : []; // If dropping into a specific folder, we need to fetch its children

      // Check for conflicts like normal uploads - same logic as Explorer drops
      const { hasFolderConflict, hasFileConflict } = checkUploadConflicts(
        prioFiles,
        targetFolderChildren
      );
      if (hasFolderConflict || hasFileConflict) {
        // Show conflict modal like normal uploads
        const { setDocumentsMetaState } = await import('../../actions/meta');
        const { DriveItemsFetchContext } = await import('../../actions');
        dispatch(
          setDocumentsMetaState({
            driveItemConflictModalState: {
              behavior: 'upload',
              context: DriveItemsFetchContext.WidgetArea,
              modelType: 'file',
              destinationProjectId: activeProjectId,
              selectedDriveItems: [],
              destinationFolderId: targetFolderId || currentFolderDriveItemId,
              sessionId,
              fileList: prioFiles,
            },
            showDriveItemConflictModalState: 'widget',
          })
        );
      } else {
        // No conflicts, start upload directly like normal uploads
        dispatch(
          sagaStartUploadFiles(
            groupId,
            sessionId,
            targetFolderId || currentFolderDriveItemId || rootDriveItemFolderId,
            activeProjectId
          )
        );

        // Add files to upload list and process like normal uploads
        dispatchUploadFiles({
          dispatch,
          groupId,
          sessionId,
          currentFolderDriveItemId: targetFolderId || currentFolderDriveItemId,
          rootDriveItemFolderId,
          projectId: activeProjectId,
          fileList: prioFiles,
          addPrioFilesToUploadList,
          sagaUploadFiles,
          conflictBehaviour: 'rename',
        });
      }
    } catch (error) {
      console.error('Error uploading Proman report:', error);
      notification.error({
        message: t('common:error'),
        description: t('documents:errorMessages.fileUploadError', {
          name: promanItem.fileName,
        }),
      });
    }
  };

  const onDragEnd = useCallback(
    async (item, monitor) => {
      if (monitor.didDrop()) {
        const dndDriveItemDto = monitor.getItem() as DndDriveItemDto;
        const dropResult = monitor.getDropResult() as DndDriveItemDropResult;
        if (dropResult?.type === 'project') {
          moveOrCopyDriveItems(
            dispatch,
            'widget',
            dndDriveItemDto.driveItems,
            dndDriveItemDto.sourceProjectId,
            dndDriveItemDto.sourceFolderId,
            (dropResult.object as Project).projectId,
            'root-group-' + (dropResult.object as Project).groupId,
            altIsPressed ? 'copy' : 'move'
          );
          setSelectedDriveItems(
            selectedDriveItems.filter(
              (item) =>
                !dndDriveItemDto.driveItems.find(
                  (itemForCopyOrMove) => itemForCopyOrMove.id === item.id
                )
            )
          );
        }
        if (dropResult?.type === 'driveItem') {
          const driveItemFolderDropResult =
            dropResult.object as FolderDriveItem;
          const hasConflicts = await checkDriveItemNameConflicts(
            driveItemFolderDropResult.groupId,
            driveItemFolderDropResult.driveItem.id,
            dndDriveItemDto.driveItems
          );
          if (hasConflicts) {
            moveOrCopyDriveItems(
              dispatch,
              'widget',
              dndDriveItemDto.driveItems,
              dndDriveItemDto.sourceProjectId,
              dndDriveItemDto.sourceFolderId,
              driveItemFolderDropResult.projectId,
              driveItemFolderDropResult.driveItem.id as DriveItemId,
              altIsPressed ? 'copy' : 'move'
            );
          } else {
            dispatch(
              copyItemsIntoFolder(
                dndDriveItemDto.sourceProjectId,
                dndDriveItemDto.sourceGroupId,
                driveItemFolderDropResult.groupId,
                dndDriveItemDto.driveItems.map((item) => ({
                  driveItemId: item.id,
                  deleteSourceDriveItem: !altIsPressed,
                  name: item.name,
                })),
                dndDriveItemDto.driveItems,
                dndDriveItemDto.sourceFolderId,
                driveItemFolderDropResult.driveItem.id,
                false,
                driveItemFolderDropResult.driveItem?.name === 'root',
                (!altIsPressed ? 'replace' : 'rename') as any
              )
            );
            dispatch(
              fetchDriveItemsSagaAction(
                driveItemFolderDropResult.projectId,
                driveItemFolderDropResult.groupId,
                driveItemFolderDropResult.driveItem.id,
                driveItemFolderDropResult.driveItem?.name === 'root',
                DriveItemsFetchContext.WidgetArea
              )
            );
            if (
              !altIsPressed &&
              dndDriveItemDto.sourceProjectId &&
              dndDriveItemDto.sourceGroupId &&
              dndDriveItemDto.sourceFolderId
            ) {
              dispatch(
                fetchDriveItemsSagaAction(
                  dndDriveItemDto.sourceProjectId,
                  dndDriveItemDto.sourceGroupId,
                  dndDriveItemDto.sourceFolderId,
                  false,
                  DriveItemsFetchContext.WidgetArea
                )
              );
            }
            if (onDndMove) {
              onDndMove(item, monitor);
            }
            if (onDndCopy && altIsPressed) {
              onDndCopy(item, monitor);
            }
            setSelectedDriveItems(
              selectedDriveItems.filter(
                (item) =>
                  !dndDriveItemDto.driveItems.find(
                    (itemForCopyOrMove) => itemForCopyOrMove.id === item.id
                  )
              )
            );
          }
        }
        if (dropResult?.type === 'remoteDriveItem') {
          const remoteDriveItemDropResult =
            dropResult.object as DriveUserRemoteItem;
          const isDestinationRoot = remoteDriveItemDropResult.isRootFolder;

          const isDraggedItemSelected = dndDriveItemDto.driveItems.some(
            (x) => x.id === dndDriveItemDto.driveItem.id
          );
          const itemsForCopyOrMove = () => {
            if (!isDraggedItemSelected)
              return [
                {
                  name: dndDriveItemDto.driveItem.name,
                  driveItemId: dndDriveItemDto.driveItem.id,
                  deleteSourceDriveItem: altIsPressed ? false : true,
                },
              ];
            return dndDriveItemDto.driveItems
              ? dndDriveItemDto.driveItems.map((item) => ({
                  name: item.name,
                  driveItemId: item.id,
                  deleteSourceDriveItem: altIsPressed ? false : true,
                }))
              : [];
          };

          const originalItemsForCopyOrMove = () => {
            if (!isDraggedItemSelected) return [dndDriveItemDto.driveItem];
            return dndDriveItemDto.driveItems ? dndDriveItemDto.driveItems : [];
          };

          if (
            !(
              itemsForCopyOrMove()?.[0]?.deleteSourceDriveItem &&
              (dndDriveItemDto.sourceFolderId ===
                remoteDriveItemDropResult.remoteItemId ||
                (isDestinationRoot &&
                  dndDriveItemDto.root.sourceIsRoot &&
                  groupId === remoteDriveItemDropResult.groupId))
            )
          ) {
            moveOrCopyDriveItems(
              dispatch,
              'widget',
              originalItemsForCopyOrMove(),
              dndDriveItemDto.sourceProjectId,
              dndDriveItemDto.sourceFolderId,
              remoteDriveItemDropResult.projectId,
              remoteDriveItemDropResult.remoteItemId as DriveItemId,
              altIsPressed ? 'copy' : 'move'
            );
          }

          setSelectedDriveItems(
            selectedDriveItems.filter(
              (item) =>
                !itemsForCopyOrMove().find(
                  (itemForCopyOrMove) =>
                    itemForCopyOrMove.driveItemId === item.id
                )
            )
          );

          // onDndMove(item, monitor);
          // handleChangeDriveItems && handleChangeDriveItems([]);
        }
      }
    },
    [
      dispatch,
      selectedDriveItems,
      setSelectedDriveItems,
      onDndMove,
      onDndCopy,
      groupId,
      altIsPressed,
    ]
  );

  const onOver = (isOver: boolean) => {
    onRowOver(isOver);
  };

  const onChange = (items: DriveItem[]) => {
    setSelectedDriveItems(items);
    //handleChangeDriveItems && handleChangeDriveItems(items);
  };

  const onDriveItemClick = (driveItem: DriveItem, mimeType: string) => {
    const isOneNoteFile = checkIfOneNote(driveItem);
    const isFolder = isDriveItemFolder(driveItem) && !isOneNoteFile;
    if (isFolder) {
      setSelectedDriveItems([]);
    }
    if (isFolder || (isKeywordSearch && isFolder)) {
      handleDriveItemClick(driveItem, mimeType);
      setIsKeywordSearch(false);
      setIsSearchingDocuments(false);
      setSearchInput(null);
      setSearchDriveItemId(null);
    }
  };

  const onDriveItemDoubleClick = (
    driveItem: DriveItem,
    e: React.MouseEvent
  ) => {
    const isPdf = getPDFType(driveItem) === 'pdf';
    if (
      documentsSettings &&
      documentsSettings.doubleClickOpenBehaviour === 'local'
    ) {
      openLocally(driveItem, isPdf);
    }
    if (
      documentsSettings &&
      documentsSettings.doubleClickOpenBehaviour === 'browser'
    ) {
      openInBrowser(driveItem);
    }
  };

  const triggerFunctions = (
    record: DriveItem,
    index
  ): HTMLAttributes<HTMLElement> => {
    return {
      onClick: (event) => {
        event.preventDefault();
        if (onDriveItemClick) {
          if (record.id) {
            onDriveItemClick(record, record?.file?.mimeType);
          }
        }
      },

      onDoubleClick: onDriveItemDoubleClick
        ? (e) => {
            e.preventDefault();
            record.id && onDriveItemDoubleClick(record, e);
          }
        : (e) => e.preventDefault(),
    };
  };

  const searchOnline = useCallback(() => {
    return { searchTerm: searchInput };
  }, [searchInput]);

  const getDriveItems = useCallback(() => {
    if (noResult) {
      return [];
    }
    if (searchOnline().searchTerm) {
      if (
        searchOnline &&
        searchOnline().searchTerm.trim() !== '' &&
        isKeywordSearch
      ) {
        return driveItemsFromSearch ?? [];
      }
    }
    return driveItems ?? [];
  }, [
    driveItemsFromSearch,
    driveItems,
    searchOnline,
    noResult,
    isKeywordSearch,
  ]);

  //#endregion

  //#region -------------------------------- Hooks
  useEffect(() => {
    dispatch(
      fetchDriveItemsSagaAction(
        activeProjectId,
        groupId,
        currentFolderDriveItemId,
        isRoot,
        DriveItemsFetchContext.WidgetArea
      )
    );
    setSelectedDriveItems([]);
  }, [dispatch, groupId, isRoot, currentFolderDriveItemId, activeProjectId]);

  useEffect(() => {
    setIsKeywordSearch(false);
    setNoResult(false);

    if (!!groupId) {
      if (searchInput) {
        const triggerOnlineSearch = async (
          searchTerm: string
        ): Promise<void> => {
          const projectIdFromSearch = searchTerm.match(
            /(?<=projects\/)(.*)(?=\/documents)/
          )?.[0];
          const driveItemsFolderIdFromSearch = searchTerm.match(
            /(?<=\/documents\/folder\/)(.*)(?=)/
          )?.[0];
          const isPrioLink = !!projectIdFromSearch;

          if (isPrioLink) {
            setProjectIdFromUrl(projectIdFromSearch);
            setSearchDriveItemId(
              driveItemsFolderIdFromSearch ?? rootDriveItemFolderId
            );
            if (projectsById[projectIdFromSearch]) {
              dispatch(
                fetchDriveItemsSagaAction(
                  activeProjectId,
                  projectsById[projectIdFromSearch].groupId,
                  driveItemsFolderIdFromSearch ?? rootDriveItemFolderId,
                  driveItemsFolderIdFromSearch === 'all',
                  DriveItemsFetchContext.WidgetArea
                )
              );
            }
          } else {
            try {
              setIsKeywordSearch(true);
              const { data } = await apiFetchDocumentSearchResults(
                groupId,
                searchTerm.trim()
              );
              if (data?.children && Array.isArray(data.children)) {
                setDriveItemsFromSearch(data.children);
              } else {
                setNoResult(true);
              }
            } catch (error) {
              console.error(
                'Error while fetching document search results:',
                error
              );
            }
          }
        };

        const { searchTerm } = searchOnline();
        triggerOnlineSearch(searchTerm);
      } else {
        setDriveItemsFromSearch(null);
        setSearchDriveItemId(null);
      }
      setSelectedDriveItems([]);
    }
  }, [
    projectsById,
    activeProjectId,
    groupId,
    searchInput,
    dispatch,
    rootDriveItemFolderId,
    searchOnline,
    setIsKeywordSearch,
    setProjectIdFromUrl,
    setSearchDriveItemId,
    t,
  ]);

  const onUnzipArchive = useCallback(
    async (driveItem: DriveItem) => {
      if (driveItem) {
        const { result } = await apiUnzipArchive(
          groupId,
          driveItem,
          currentFolderDriveItemId
        );
        if (result.status >= 200 && result.status < 300) {
          notification.open({
            message: t('common:info'),
            description: t(
              'documents:inProgressMessage.unzipArchiveIsInProgress',
              {
                name: driveItem?.name,
              }
            ),
          });
        } else {
          notification.open({
            message: t('common:error'),
            description: `${t('documents:errorMessages.unzipArchiveError', {
              name: driveItem?.name,
            })}${result.statusText ? `: ${result.statusText}` : ''}`,
          });
        }
      }
    },
    [groupId, currentFolderDriveItemId, t]
  );

  const onMenuFolderZip = useCallback(async (driveItem: DriveItem) => {
    setZipDriveItem(driveItem);
    setZipModalVisible(true);
  }, []);

  const onConfirmFolderZip = useCallback(
    async (zipName: string) => {
      if (zipDriveItem) {
        await folderZip(groupId, zipDriveItem, zipName);
      }
      setZipModalVisible(false);
      setZipDriveItem(null);
    },
    [groupId, zipDriveItem]
  );

  const onCancelFolderZip = useCallback(() => {
    setZipModalVisible(false);
    setZipDriveItem(null);
  }, []);
  //#endregion

  //#region -------------------------------- Components
  const noItemsErrorScreen = () => (
    <Flex.Column
      childrenGap={8}
      alignItems="center"
      justifyContent="center"
      flex={1}
    >
      <Flex.Item display="flex" width={40} height={40}>
        <FontAwesomeIcon
          icon={['fad', 'folder-xmark']}
          style={{
            width: '100%',
            height: '100%',
            //@ts-ignore
            '--fa-primary-color': theme.old.palette.chromaticPalette.red,
            '--fa-secondary-color': theme.old.palette.chromaticPalette.yellow,
            '--fa-primary-opacity': 1,
            '--fa-secondary-opacity': 1,
          }}
        />
      </Flex.Item>
      <Flex.Item>{errorMessage}</Flex.Item>
    </Flex.Column>
  );

  const noItemsScreen = () => (
    <div className={classes.noItemsScreen}>
      <div className={classes.noItemsIconContainer}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className={classes.noItemsText}>
              {t('documents:table.noItems')}
            </span>
          }
        />
      </div>
    </div>
  );
  const onOpenMessage = useCallback(
    async (driveItem: DriveItem) => {
      const messageIds =
        driveItem.listItemFields?.Prio365AttachedMessageImmutableId;

      const { data } = await apiFetchSavedAttachmentMetadata(messageIds);

      if (data && data.length > 0) {
        const { projectId, messageImmutableId } = data[0];

        const width = window.screen.availWidth / 2;
        const height = window.screen.availHeight / 2;
        window.open(
          `/view/${
            projectId.length === 0 ? 'me' : projectId
          }/message/${messageImmutableId}/details`,
          '_blank',
          `width=${width},height=${height},noopener,noreferrer`
        );
      } else {
        notification.open({
          message: t('common:error'),
          description: t(
            'mail:errorMessages.messages.savedAttachmentMetadataError'
          ),
        });
      }
    },
    [t]
  );

  const menu = useCallback(
    (entry: DriveItem) => {
      const oneGigaByteInBytes = 1073741824;
      const isUnzippable = () => {
        const whenSearching = isKeywordSearch
          ? entry.parentReference.id === rootFolderDriveItem?.id
            ? false
            : true
          : !isRoot;
        return (
          entry?.file?.mimeType === 'application/zip' &&
          whenSearching &&
          entry.size / oneGigaByteInBytes <= 2
        );
      };

      return (
        <DocumentsTableContextMenu
          className={classes.menuButton}
          project={projectsById[activeProjectId]}
          iconClassname={classes.rowMenuButtonIcon}
          driveItem={entry}
          parentIsRoot={isRoot}
          onMenuRename={onMenuRename}
          onMenuCopy={onMenuCopy}
          onMenuMove={onMenuMove}
          onMenuDelete={onMenuDelete}
          onCopyAsPdf={onCopyAsPdf}
          onMenuDownload={onMenuDownload}
          onUnzipArchive={isUnzippable() ? onUnzipArchive : undefined}
          onMenuFolderZip={onMenuFolderZip}
          openParentFolder={onOpenParentFolder}
          openInBrowser={activeProjectId ? openInBrowser : undefined}
          openLocally={activeProjectId ? openLocally : undefined}
          isSearchingDocumentsInWidgetBar={isSearchingDocuments}
          onMenuEditFolderAccessRights={onEditFolderAccessRights}
          onDownloadXInvoice={onDownloadXInvoice}
        />
      );
    },
    [
      classes,
      activeProjectId,
      isRoot,
      onMenuDownload,
      onMenuDelete,
      onMenuRename,
      onMenuMove,
      onCopyAsPdf,
      onMenuCopy,
      openInBrowser,
      openLocally,
      onUnzipArchive,
      onOpenParentFolder,
      isSearchingDocuments,
      rootFolderDriveItem?.id,
      isKeywordSearch,
      onEditFolderAccessRights,
      projectsById,
      onDownloadXInvoice,
      onMenuFolderZip,
    ]
  );
  //#endregion

  //#region ------------------------------ ColumnProps
  const columns: VColumn<DriveItem>[] = useMemo(
    () => [
      {
        id: 'file',
        alignSelf: true,
        width: 15,
        title: t('documents:table.columnTitle.isFolder'),
        accessor: 'file',
        sortType: (rowA, rowB) => {
          return iconForFile(rowA.original).localeCompare(
            iconForFile(rowB.original)
          );
        },
        className: classes.columnFile,
        Cell: (cellProps) => (
          <DocumentsTableRowIcon
            classes={documentsTableRowIconClasses}
            driveItem={cellProps?.row?.original}
            groupId={groupId}
            projectId={activeProjectId}
            driveItemList={cellProps?.rows?.map((x) => x.original) ?? []}
          />
        ),
      },
      {
        id: 'name',
        alignSelf: true,
        title: t('documents:table.columnTitle.name'),
        accessor: 'name',
        innerStyle: { textOverflow: 'ellipsis' },
        width: 85,
        className: classes.columnName,
        Cell: (cellProps) => (
          <Flex.Row alignItems="center">
            <div
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {cellProps.row.original.name}
            </div>
            {!!cellProps?.row?.original.listItemFields
              ?.Prio365AttachedMessageId?.[0] && (
              <div
                style={{
                  height: theme.old.components.documentsTable.tableRowHeight,
                }}
              >
                <Button
                  type="default"
                  onClick={() => {
                    onOpenMessage(cellProps?.row?.original);
                  }}
                >
                  <div className="fa-layers">
                    <FontAwesomeIcon
                      icon={['fas', 'circle']}
                      mask={['fal', 'envelope']}
                      className={classes.maskedIcon}
                    />
                    <FontAwesomeIcon
                      icon={['fal', 'link']}
                      style={{ transform: 'scale(.75) translate(.5em, .25em)' }}
                    />
                  </div>
                </Button>
              </div>
            )}
            <div
              style={{
                height: theme.old.components.documentsTable.tableRowHeight,
              }}
            >
              {menu(cellProps.row.original)}
            </div>
          </Flex.Row>
        ),
        sortType: (rowA, rowB) => {
          return rowA.original.name.localeCompare(rowB.original.name);
        },
      },
    ],
    [
      t,
      classes,
      menu,
      groupId,
      theme.old.components.documentsTable.tableRowHeight,
      activeProjectId,
      onOpenMessage,
      documentsTableRowIconClasses,
    ]
  );
  //#endregion

  return (
    <div
      ref={drop}
      className={classNames(classes.root, { 'drop-over': isOver })}
      style={{
        backgroundColor: isOver ? 'rgba(66, 133, 244, 0.05)' : undefined,
        borderRadius: isOver ? '4px' : undefined,
        transition: 'background-color 0.2s ease',
      }}
    >
      <VirtualTable2
        className={classes.vTable}
        id={tableId}
        columnRowHeight={
          theme.old.components.documentsTable.tableColumnRowHeight
        }
        selected={(driveItem) =>
          selectedDriveItems.some((x) => x.id === driveItem.id)
        }
        columns={columns}
        data={getDriveItems()}
        rowsAreSelectable
        overscanRowCount={10}
        columnsResizable
        loading={getLoading()}
        noItemsScreen={errorMessage ? noItemsErrorScreen() : noItemsScreen()}
        onRow={{
          onRowDrag: {
            dragType: DND_TYPE_DRIVE_ITEM_FILE,
            configureDraggedObject: (
              draggedItem: DriveItem,
              selectedDriveItems: DriveItem[]
            ) => {
              return {
                dropEffect: altIsPressed ? 'copy' : 'move',
                driveItem: draggedItem,
                driveItems:
                  selectedDriveItems.length === 0 ||
                  !selectedDriveItems.find(
                    (selectedItem) => selectedItem.id === draggedItem.id
                  )
                    ? [draggedItem]
                    : selectedDriveItems,
                sourceGroupId: groupId,
                sourceFolderId:
                  currentFolderDriveItemId ?? `root-group-${groupId}`,
                sourceProjectId: activeProjectId,
                root: {
                  sourceFolderRootId:
                    currentFolderDriveItemId ?? `root-group-${groupId}`,
                  sourceIsRoot: isRoot,
                },
              } as DndDriveItemDto;
            },
            onDragEnd: onDragEnd,
          },
          onRowDrop: {
            configureDroppableObject: (
              targetDropItem: DriveItem,
              selectedDriveItems: DriveItem[]
            ) =>
              ({
                projectId: activeProjectId,
                groupId: groupId,
                driveItem: targetDropItem,
              }) as FolderDriveItem,

            including: (x) => isDriveItemFolder(x),
            disable: (item, monitor, targetDropItem: FolderDriveItem) => {
              return false;
            },
            acceptType: [
              DND_TYPE_DRIVE_ITEM_FILE,
              DND_TYPE_EMAIL,
              DND_TYPE_EMAIL_ATTACHMENT,
              DND_TYPE_PROMAN_REPORT,
            ],
            onDrop: onDrop,
            onOver: onOver,
          },
          triggerFunctions: triggerFunctions,
        }}
        onChange={onChange}
        dataToForceRender={dataToForceRender}
        classNameTableRow={rowClass}
        rowHeight={theme.old.components.documentsTable.tableRowHeight}
      />
      <ZipFolderModal
        visible={zipModalVisible}
        defaultName={zipDriveItem?.name || 'archiv'}
        onOk={onConfirmFolderZip}
        onCancel={onCancelFolderZip}
      />
    </div>
  );
};

export default DocumentsWidgetTable;
